/**
 * Admin Panel — Twilio Operator Console API
 *
 * Zod schemas for the operator console over the whole Twilio fleet.
 *
 * Part of the NexusApi system for end-to-end type safety.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  AdminTwilioConsoleAvailableTypesQuerySchema,
  AdminTwilioConsoleAvailableTypesResponseSchema,
  AdminTwilioConsoleBuyNumberBodySchema,
  AdminTwilioConsoleBuyNumberResponseSchema,
  AdminTwilioConsoleCancelPendingReleaseBodySchema,
  AdminTwilioConsoleCancelPendingReleaseResponseSchema,
  AdminTwilioConsoleConfigureNumberBodySchema,
  AdminTwilioConsoleConfigureNumberResponseSchema,
  AdminTwilioConsoleCreateTemplateBodySchema,
  AdminTwilioConsoleCreateTemplateResponseSchema,
  AdminTwilioConsoleDeleteTemplateResponseSchema,
  AdminTwilioConsoleListAccountsResponseSchema,
  AdminTwilioConsoleListBundlesResponseSchema,
  AdminTwilioConsoleListMessagesQuerySchema,
  AdminTwilioConsoleListMessagesResponseSchema,
  AdminTwilioConsoleListNumbersResponseSchema,
  AdminTwilioConsoleListPendingReleasesResponseSchema,
  AdminTwilioConsoleListTemplatesResponseSchema,
  AdminTwilioConsoleListWhatsappSendersResponseSchema,
  AdminTwilioConsoleMarkForReleaseBodySchema,
  AdminTwilioConsoleMarkForReleaseResponseSchema,
  AdminTwilioConsoleOverviewResponseSchema,
  AdminTwilioConsoleRepointWhatsappWebhookBodySchema,
  AdminTwilioConsoleRepointWhatsappWebhookResponseSchema,
  AdminTwilioConsoleSearchNumbersQuerySchema,
  AdminTwilioConsoleSearchNumbersResponseSchema,
  AdminTwilioConsoleSubmitTemplateApprovalBodySchema,
  AdminTwilioConsoleSubmitTemplateApprovalResponseSchema,
  AdminTwilioConsoleTemplateOnAccountPathVarsSchema,
  AdminTwilioConsoleTransferNumberBodySchema,
  AdminTwilioConsoleTransferNumberResponseSchema,
  AdminTwilioConsoleTransferPreflightResponseSchema
} from "./schemas/twilio-console.schema";

// ============================================================
// ZAdminTwilioConsole — Endpoint Definitions
// ============================================================

export const ZAdminTwilioConsole = {
  /**
   * Fleet overview — the console's landing read.
   * GET /api/admin/twilio-console/overview
   *
   * Reads the live account list from Twilio and reconciles it against the
   * TWILIO ApiKeyConnections we store, so both directions of drift are visible
   * in one answer.
   */
  Overview: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/overview",
    Response: ApiSuccessSchema(AdminTwilioConsoleOverviewResponseSchema)
  },

  /**
   * Every regulatory bundle we can read, plus the accounts we cannot.
   * GET /api/admin/twilio-console/compliance/bundles
   *
   * Coverage is the master account plus subaccounts with stored credentials —
   * Twilio answers 401 for a subaccount authenticated with the parent's token.
   * The response carries `unreadable[]` so a gap never reads as an empty result.
   */
  ListBundles: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/compliance/bundles",
    Response: ApiSuccessSchema(AdminTwilioConsoleListBundlesResponseSchema)
  },

  /**
   * Every phone number on every account, with its webhooks.
   * GET /api/admin/twilio-console/numbers
   *
   * Genuinely fleet-wide: the classic API accepts the parent auth token for a
   * subaccount's numbers, so coverage is all accounts rather than the three we
   * hold credentials for.
   */
  ListNumbers: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/numbers",
    Response: ApiSuccessSchema(AdminTwilioConsoleListNumbersResponseSchema)
  },

  /**
   * Which number types Twilio supports in a country.
   * GET /api/admin/twilio-console/numbers/available/types
   *
   * Priced from the Pricing API, not probed from inventory: a type stays
   * SUPPORTED in a country during a stock shortage, and answering "unavailable"
   * to a temporary gap would make the operator stop looking for a type they can
   * actually have.
   */
  AvailableTypes: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/numbers/available/types",
    Query: AdminTwilioConsoleAvailableTypesQuerySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleAvailableTypesResponseSchema)
  },

  /**
   * Search Twilio's purchasable inventory.
   * GET /api/admin/twilio-console/numbers/available
   *
   * The read half of the one action that leaves Nexus today: with no way to buy
   * from here, an operator approving a phone-number request can only pick from
   * numbers someone already bought by hand on twilio.com.
   *
   * Master-scoped deliberately — available inventory is Twilio's global stock,
   * the same whichever account asks. The account only matters at purchase, so it
   * is a field on `BuyNumber` rather than on this search.
   */
  SearchAvailableNumbers: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/numbers/available",
    Query: AdminTwilioConsoleSearchNumbersQuerySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleSearchNumbersResponseSchema)
  },

  /**
   * Buy a number onto an account.
   * POST /api/admin/twilio-console/numbers/buy
   *
   * Irreversible AND billable — the only action in the console that is both.
   * Release has a cooling window precisely because it can be undone up to the
   * moment it fires; a purchase has no equivalent, because the money is spent
   * the instant Twilio answers. `acknowledgedCost` is enforced here for the same
   * reason `TransferNumber.acknowledgedRisks` is: the UI gate is not the gate.
   */
  BuyNumber: {
    method: "POST" as const,
    path: "/api/admin/twilio-console/numbers/buy",
    Body: AdminTwilioConsoleBuyNumberBodySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleBuyNumberResponseSchema)
  },

  /**
   * Point a number's voice, SMS and status-callback hooks somewhere else.
   * PATCH /api/admin/twilio-console/numbers/:sid/webhooks
   *
   * PATCH, not PUT, and the verb is the contract: an omitted field is left
   * alone, an explicit `null` clears it. See the body schema.
   *
   * The only Numbers mutation in this shape. It costs nothing, it is undone by
   * writing the previous value back, and the response carries that previous
   * value — so it needs no cooling window and no preflight beyond showing the
   * operator what the hook is today. Buy, transfer and release are each a
   * different risk class and are wired separately.
   */
  ConfigureNumber: {
    method: "PATCH" as const,
    path: "/api/admin/twilio-console/numbers/:sid/webhooks",
    PathVars: z.object({ sid: z.string().min(1) }),
    Body: AdminTwilioConsoleConfigureNumberBodySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleConfigureNumberResponseSchema)
  },

  /**
   * Every Twilio account, for a destination picker.
   * GET /api/admin/twilio-console/accounts
   */
  ListAccounts: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/accounts",
    Response: ApiSuccessSchema(AdminTwilioConsoleListAccountsResponseSchema)
  },

  /**
   * What Nexus's own records say could break if this number transfers.
   * GET /api/admin/twilio-console/numbers/:sid/transfer-preflight
   *
   * `accountSid` is a query param, not folded into the path, because it plays
   * the same "echo the account you're viewing" role as `ConfigureNumber`'s body
   * field — the backend re-fetches the number on this account and refuses if it
   * has moved, so a client working from a stale fleet list fails the read
   * instead of preflighting the wrong account's number.
   */
  TransferPreflight: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/numbers/:sid/transfer-preflight",
    PathVars: z.object({ sid: z.string().min(1) }),
    Query: z.object({ accountSid: z.string().min(1) }),
    Response: ApiSuccessSchema(AdminTwilioConsoleTransferPreflightResponseSchema)
  },

  /**
   * Move a number to another account.
   * POST /api/admin/twilio-console/numbers/:sid/transfer
   *
   * The one Numbers mutation with a real cost: four risks (webhooks, A2P,
   * toll-free verification, WhatsApp binding) break silently, and Twilio never
   * reports that they did. See `TransferPreflight` for what surfaces them
   * beforehand, and `AdminTwilioConsoleTransferNumberBodySchema.acknowledgedRisks`
   * for why the confirmation is enforced here too, not only in the UI.
   */
  TransferNumber: {
    method: "POST" as const,
    path: "/api/admin/twilio-console/numbers/:sid/transfer",
    PathVars: z.object({ sid: z.string().min(1) }),
    Body: AdminTwilioConsoleTransferNumberBodySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleTransferNumberResponseSchema)
  },

  /**
   * Every number currently PENDING_RELEASE, for the console's list/cancel UI.
   * GET /api/admin/twilio-console/numbers/pending-releases
   *
   * DB-only — no live Twilio call, unlike ListNumbers. This is entirely about
   * OUR OWN state (what got marked, and when the sweep cron will act on it).
   */
  ListPendingReleases: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/numbers/pending-releases",
    Response: ApiSuccessSchema(AdminTwilioConsoleListPendingReleasesResponseSchema)
  },

  /**
   * Mark a number for release. Never touches Twilio — starts a cooling
   * window; the sweep cron actually releases it once that expires, unless
   * cancelled first.
   * POST /api/admin/twilio-console/numbers/:sid/mark-for-release
   */
  MarkForRelease: {
    method: "POST" as const,
    path: "/api/admin/twilio-console/numbers/:sid/mark-for-release",
    PathVars: z.object({ sid: z.string().min(1) }),
    Body: AdminTwilioConsoleMarkForReleaseBodySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleMarkForReleaseResponseSchema)
  },

  /**
   * Cancel a pending release — reverts to ACTIVE, clears the cooldown. Valid
   * any time before the sweep cron actually releases the number on Twilio.
   * POST /api/admin/twilio-console/numbers/:sid/cancel-pending-release
   */
  CancelPendingRelease: {
    method: "POST" as const,
    path: "/api/admin/twilio-console/numbers/:sid/cancel-pending-release",
    PathVars: z.object({ sid: z.string().min(1) }),
    Body: AdminTwilioConsoleCancelPendingReleaseBodySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleCancelPendingReleaseResponseSchema)
  },

  /**
   * Every WhatsApp sender, the deployment each one actually serves, and where
   * its inbound webhook points.
   * GET /api/admin/twilio-console/whatsapp/senders
   *
   * DATABASE-FIRST, unlike ListNumbers, and the difference is forced rather than
   * stylistic. Numbers are fleet-wide because the classic API accepts the parent
   * auth token for any subaccount; the WhatsApp Senders API is a different host
   * that takes only the SUBACCOUNT's own token, so there is no master read to
   * make. `WhatsappSender` is therefore the fleet index, and Twilio is consulted
   * per row for live state — which is also why a failed live read lands on the
   * row as `liveReadError` instead of in a fleet-level `unreadable[]`.
   *
   * This answers the question an operator could not answer during an outage:
   * "which number does this deployment serve, and is its webhook pointed at
   * something that will survive a reassign?"
   */
  ListWhatsappSenders: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/whatsapp/senders",
    Response: ApiSuccessSchema(AdminTwilioConsoleListWhatsappSendersResponseSchema)
  },

  /**
   * Point one sender's webhook back at its connection's canonical route.
   * POST /api/admin/twilio-console/whatsapp/senders/:senderSid/repoint-webhook
   *
   * The repair for what the list flags. Idempotent, and needs no confirmation
   * gate: unlike buy or transfer it costs nothing, targets a URL the client
   * cannot influence, and the only state it can overwrite is a hook that is
   * already wrong by construction.
   */
  RepointWhatsappWebhook: {
    method: "POST" as const,
    path: "/api/admin/twilio-console/whatsapp/senders/:senderSid/repoint-webhook",
    PathVars: z.object({ senderSid: z.string().min(1) }),
    Body: AdminTwilioConsoleRepointWhatsappWebhookBodySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleRepointWhatsappWebhookResponseSchema)
  },

  /**
   * Every WhatsApp message template on every account, with Meta's verdict on it.
   * GET /api/admin/twilio-console/whatsapp/templates
   *
   * Fleet-wide by FAN-OUT, not by a master read — the Content API client is
   * constructed from one account's credentials, so a template is only visible to
   * the account that owns it. That puts this view in the same shape as
   * `ListBundles` rather than `ListNumbers`: coverage is whatever we hold
   * credentials for, and `unreadable[]` names every account we could not ask so
   * a credential gap never reads as "this client has no templates".
   *
   * Approval state is joined in from Twilio's content-and-approvals list rather
   * than fetched per template. Without it the console would list templates and
   * be unable to say which ones Meta will actually deliver — which is the whole
   * question an operator opens this page to answer.
   */
  ListTemplates: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/whatsapp/templates",
    Response: ApiSuccessSchema(AdminTwilioConsoleListTemplatesResponseSchema)
  },

  /**
   * Create a template on one account.
   * POST /api/admin/twilio-console/whatsapp/templates
   *
   * Cheap and reversible — a Content resource is free and `DeleteTemplate`
   * removes it — so unlike `BuyNumber` this needs no acknowledgement gate. The
   * irreversible half is `SubmitTemplateApproval`, kept as a separate call for
   * exactly that reason.
   */
  CreateTemplate: {
    method: "POST" as const,
    path: "/api/admin/twilio-console/whatsapp/templates",
    Body: AdminTwilioConsoleCreateTemplateBodySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleCreateTemplateResponseSchema)
  },

  /**
   * Remove a template from an account.
   * DELETE /api/admin/twilio-console/whatsapp/accounts/:accountSid/templates/:templateSid
   *
   * Account-scoped in the PATH, unlike `ConfigureNumber` (body) and
   * `TransferPreflight` (query), and the divergence is forced by the generated
   * client rather than chosen: a mutation hook carries `PathVars` and `Body` but
   * DROPS `Query`, so an account passed as a query param would go missing from
   * every `useDeleteTemplate` call with nothing in the type system to catch it.
   * See `AdminTwilioConsoleTemplateOnAccountPathVarsSchema`.
   *
   * Deleting an APPROVED template does not un-approve it on Meta's side, and the
   * replacement is a new Content SID that must be submitted from scratch. The UI
   * should say so before it fires this.
   */
  DeleteTemplate: {
    method: "DELETE" as const,
    path: "/api/admin/twilio-console/whatsapp/accounts/:accountSid/templates/:templateSid",
    PathVars: AdminTwilioConsoleTemplateOnAccountPathVarsSchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleDeleteTemplateResponseSchema)
  },

  /**
   * Send an existing template to Meta for approval.
   * POST /api/admin/twilio-console/whatsapp/accounts/:accountSid/templates/:templateSid/submit-approval
   *
   * Shares `DeleteTemplate`'s account-scoped path so the two writes address a
   * template identically — the body then carries only what META needs, and there
   * is no second copy of `accountSid` for a client to contradict.
   *
   * Single-shot: a Content SID can be submitted exactly once, and Twilio refuses
   * every re-submission. A wrong name or category is therefore not correctable —
   * it is replaced by a whole new template. Idempotent in the useful direction
   * though: a repeat call reports the existing status with `alreadySubmitted`
   * rather than erroring, so a double-click is safe.
   */
  SubmitTemplateApproval: {
    method: "POST" as const,
    path: "/api/admin/twilio-console/whatsapp/accounts/:accountSid/templates/:templateSid/submit-approval",
    PathVars: AdminTwilioConsoleTemplateOnAccountPathVarsSchema,
    Body: AdminTwilioConsoleSubmitTemplateApprovalBodySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleSubmitTemplateApprovalResponseSchema)
  },

  /**
   * Every message, inbound and outbound, across the master account and every
   * subaccount, over one window.
   * GET /api/admin/twilio-console/messages
   *
   * Fleet-wide the way `ListNumbers` is rather than the way `ListTemplates` is —
   * the classic Messages API accepts the parent auth token for a subaccount — so
   * coverage is every account, and a non-empty `unreadable[]` here is an incident
   * rather than the expected state.
   *
   * THE FIRST READ ON THIS SURFACE WHOSE RESULT SET IS NOT NATURALLY SMALL, and
   * the shape of the contract follows entirely from that. `messages.list()`
   * auto-pages, so an unbounded fan-out over the fleet walks every page of every
   * account: the window is REQUIRED and capped at
   * `TWILIO_MESSAGE_LOG_MAX_WINDOW_DAYS`, and `perAccountLimit` bounds each
   * account's read.
   *
   * That makes an incomplete answer normal, so the response carries TWO distinct
   * ways of being incomplete and never conflates them. `unreadable[]` is an
   * account we could not ask; `truncated[]` is an account we asked, read
   * correctly, and stopped short of the end of — with the instant its visible
   * window actually ends, per account. An operator who cannot tell those apart
   * will read "no failures for this client" off a page that never reached them.
   *
   * Every filter is pushed down to Twilio rather than applied to the rows
   * afterwards, which on a capped read is correctness and not speed: an
   * in-memory filter searches only what survived the cap. Twilio offers `to`,
   * `from` and the date bounds, and the account is chosen by the credentials —
   * so those are the filters, and `status`/`direction` are pointedly not, since
   * Twilio's list cannot push them down.
   */
  ListMessages: {
    method: "GET" as const,
    path: "/api/admin/twilio-console/messages",
    Query: AdminTwilioConsoleListMessagesQuerySchema,
    Response: ApiSuccessSchema(AdminTwilioConsoleListMessagesResponseSchema)
  }
} as const;

// ============================================================
// Inferred types
// ============================================================

export type AdminTwilioConsoleOverviewResponsePayload = z.infer<
  typeof AdminTwilioConsoleOverviewResponseSchema
>;

// Re-export all schemas for backend use
export * from "./schemas/twilio-console.schema";
